import { convertItemToImperial } from '@page-components/shipment';
import { CommonUtility } from '@utility/common';
import { ErrorMessageConstant } from '@utility/constants/common';
import ItemService from '@utility/services/item';
import { useEffect, useState } from 'react';
import { useDebouncedEffect } from './debounceAsync';
import PurchaseOrdersService from '@utility/services/purchase-order';
import type { IDropdownOption } from '@elements/Dropdown';
import {
  POMetadataResponseSchema,
  type POMetadataOrganization,
  type POMetadataSupplier,
} from '@schema/purchase-order/metadata';

interface IPrice {
  price: number;
  moq: number;
}

interface IMoqGroupingItem {
  _id: string;
  p_sku: string;
  p_title: string;
  p_price: number;
  upc: string;
  p_images: {
    display_image: string;
  };
  moq: number;
}

interface IMoqGrouping {
  items: IMoqGroupingItem[];
  collective_moq: number;
}

interface IRegionalItem {
  master: boolean;
  item_id: string;
  name: string;
  region_alias: string;
  currency_code: string;
  marketplace_region_id: string;
}

interface IProductImages {
  display_image: string;
  all_images: string[];
}

interface IPredictions {
  qty: number;
  statusMessage: string;
  statusMessageColor: string;
}

interface IPurchaseOrderInProgress {
  _id: string;
  qty: number;
}

interface ICartonDimension {
  unit: string;
  value: number;
}

export interface IPOAttribute {
  carton_length: ICartonDimension;
  carton_width: ICartonDimension;
  carton_height: ICartonDimension;
  carton_net_weight: ICartonDimension;
  carton_gross_weight: ICartonDimension;
  primary: boolean;
  state: string;
  units_per_carton: number;
  createdAt: string;
  _id: string;
  alias: string;
}

export interface IPOProduct {
  _id: string;
  organization_id: string;
  supplier_id: string;
  createdAt: string;
  item_upc: string;
  updatedAt: string;
  MOQ: number;
  lead_time: number;
  price: number;
  prices: IPrice[];
  moq_grouping: IMoqGrouping;
  shippable_qty?: number;
  regional_items: IRegionalItem[];
  g_attributes?: {
    short_title: string;
    key_features: string[];
  };
  unique_identifier_type: string;
  unit_of_measurement?: string;
  secondary_identifier: string;
  p_sku: string;
  p_title: string;
  p_price: number;
  marketplace_region_id: string;
  upc: string;
  p_images: IProductImages;
  predictions: IPredictions;
  purchaseOrdersInProgress: IPurchaseOrderInProgress[];
  purchaseOrdersInProgressDetails: number[];
  predictedQty: number;
  statusMessageColor: string;
  attributes: IPOAttribute[];
  currentAttributes: IPOAttribute[];
  is_NARF: boolean;
  alias: string;
  aliases?: Record<string, any>;
  assignedSuppliers: any[];
}

interface IPurchaseOrderItemsResponse {
  data: IPOProduct[];
  total_pages: number;
  total_data: number;
}

interface IUpcQty {
  [key: string]: {
    aliases: {
      [key: string]: {
        alias?: string;
        ctn_qty?: number;
      };
    };
  };
}

interface IPurchaseOrderHooksReturn {
  data: IPOProduct[];
  setData: React.Dispatch<React.SetStateAction<IPOProduct[]>>;
  loading: boolean;
  error: string;
  totalPages: number;
  filterChanged: (key: string, value: any) => void;
  pageChanged: (page: number) => void;
  filter: {
    limit: number;
    page: number;
    search: string;
  };
  totalData: number;
  refresh: () => void;
  search: string;
  setSearch: (search: string) => void;
}

export const purchaseOrderItemsHook = (
  supplierId: string,
  imperial: boolean,
  upcQty: IUpcQty,
  priceMOQ: boolean = false,
): IPurchaseOrderHooksReturn => {
  const [data, setData] = useState<IPOProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  const [filter, setFilter] = useState({
    limit: 50,
    page: 1,
    search: '',
  });

  const refresh = () => {
    setRefreshKey(Math.random());
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const reqParams = {
        ...filter,
        search: filter.search.trim(),
      };

      const result: IPurchaseOrderItemsResponse =
        await ItemService.purchaseOrderItems(supplierId, reqParams);
      const temp = result.data.map(item => {
        const t = { ...item };
        t.prices = [...(t.prices || [])].sort((a, b) => a.moq - b.moq);
        t.attributes = [];
        t.currentAttributes = [];
        if ((item.attributes || []).length > 0) {
          let temp = CommonUtility.getLatestAttribute(item);
          t.attributes = temp ? [temp] : [];
          if (upcQty[item.upc]) {
            const keyData = Object.values(upcQty[item.upc].aliases || {});
            const tempUPCItems = item.attributes.filter(x =>
              keyData.some(y => y.alias === x.alias && (y.ctn_qty ?? 0) > 0),
            );
            temp = [...tempUPCItems];
            if (tempUPCItems.length > 0) {
              t.attributes = [...tempUPCItems];
            }
          }
          t.currentAttributes = [...(item.attributes || [])];
        }
        if (priceMOQ) {
          t.MOQ = [...(item.prices || [])].sort(
            (a, b) => a.moq - b.moq,
          )?.[0]?.moq;
        }
        return t;
      });
      let _items: IPOProduct[] = [];

      if (refreshKey > 0 && imperial) {
        _items = temp.map(item => {
          item.attributes = item?.attributes?.map(att =>
            convertItemToImperial(att),
          );
          return item;
        });
      } else {
        _items = temp;
      }

      setData(
        _items.sort((a, b) => (b.predictedQty || 0) - (a.predictedQty || 0)),
      );
      setTotalPages(result.total_pages);
      setTotalData(result.total_data);
    } catch (error) {
      setError(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  useDebouncedEffect(
    () => {
      if (supplierId) {
        fetchData();
      }
    },
    [filter, refreshKey, supplierId],
    500,
  );

  const setSearch = search => {
    filterChanged('search', search);
  };

  const pageChanged = page => {
    const temp = {
      ...filter,
      page,
    };
    setFilter({ ...temp });
  };

  const filterChanged = (key, value) => {
    const temp = {
      ...filter,
      page: 1,
      [key]: value,
    };
    setFilter({ ...temp });
  };

  return {
    data,
    setData,
    loading,
    error,
    totalPages,
    filterChanged,
    pageChanged,
    filter,
    totalData,
    refresh,
    search: filter.search,
    setSearch,
  };
};

export type ISupplierOption = IDropdownOption & {
  item: POMetadataSupplier;
};

interface IPOMetadataReturn {
  data?: POMetadataOrganization;
  loading: boolean;
  error: string;
  supplierList: ISupplierOption[];
}

export const poMetaDataHook = (): IPOMetadataReturn => {
  const [data, setData] = useState<POMetadataOrganization>();
  const [supplierList, setSupplierList] = useState<ISupplierOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await PurchaseOrdersService.metadata();
      const parsed = POMetadataResponseSchema.safeParse(result);
      if (!parsed.success) {
        const first = parsed.error.issues[0];
        const path = first?.path.length ? first.path.join('.') : 'response';
        setError(
          first
            ? `Invalid purchase order metadata at ${path}: ${first.message}`
            : ErrorMessageConstant.defaultAPIError,
        );
        setData(undefined);
        setSupplierList([]);
        return;
      }

      const list = (parsed.data.data.suppliers || []).map(x => ({
        text: x.supplier_name,
        value: x._id,
        item: x,
      }));
      setSupplierList(list);
      setData(parsed.data.data);
    } catch (error) {
      setError(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    loading,
    error,
    supplierList,
  };
};
